import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, unref as _unref } from "vue"

const _hoisted_1 = { class: "header" }
const _hoisted_2 = {
  key: 0,
  class: "car-info-block"
}
const _hoisted_3 = { class: "car-info-actions" }
const _hoisted_4 = {
  key: 1,
  class: "car-info-block"
}
const _hoisted_5 = { class: "car-info-actions" }
const _hoisted_6 = { class: "timeline-item" }
const _hoisted_7 = { class: "timeline-item__property" }
const _hoisted_8 = { class: "timeline-item__property" }
const _hoisted_9 = { class: "timeline-item__property" }
const _hoisted_10 = { key: 1 }

import { ref, onMounted } from 'vue'
import { ElNotification } from 'element-plus'
import { formatDateWithTime } from '@/helpers/date'
import {
  getRegisterCarLogs,
  registerCar,
  deleteCar,
  getRegisterCarInfo,
} from '@/api/portal-car-sharing'
import CommonError from '@/components/CommonError.vue'
import Loader from '@/components/Loader.vue'
import type { EvCarProfile } from '@/models/evCarProfile'

interface PortalCarLog {
  id: string
  action: string
  description: string
  response: string
  created_at: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PortalEvCarRegistered',
  props: {
    vehicle: {}
  },
  setup(__props: any) {

const props = __props

const logs = ref<PortalCarLog[]>([])
const isLoading = ref(false)
const error = ref<string | null>(null)
const registerState = ref<'idle' | 'loading' | 'success' | 'error'>('idle')
const carInfo = ref<any | null>(null)
const carInfoLoading = ref(false)

const fetchLogs = async () => {
  isLoading.value = true
  error.value = null

  const params = { ev_car_profile_id: props.vehicle.id }

  try {
    const response = await getRegisterCarLogs(params)
    let data: PortalCarLog[] = []
    if (
      response &&
      typeof response === 'object' &&
      response !== null &&
      Object.prototype.hasOwnProperty.call(response, 'data')
    ) {
      data = (response as { data: PortalCarLog[] }).data ?? []
    }
    logs.value = data
  } catch (e) {
    if (
      typeof e === 'object' &&
      e &&
      'response' in e &&
      e.response &&
      typeof e.response === 'object' &&
      'data' in e.response &&
      e.response.data &&
      typeof e.response.data === 'object' &&
      'message' in e.response.data
    ) {
      error.value =
        (e.response.data as { message?: string }).message ||
        'Error loading logs'
    } else if (e instanceof Error) {
      error.value = e.message
    } else {
      error.value = 'Error loading logs'
    }
  } finally {
    isLoading.value = false
  }
}

const handleRegisterCar = async () => {
  registerState.value = 'loading'
  try {
    const payload = {
      car_number: props.vehicle.plate_number,
      type: 'ev' as const,
      ev_car_profile_id: props.vehicle.id,
    }
    await registerCar(payload)
    ElNotification({
      title: 'Success',
      message: 'EV Car registered successfully!',
      type: 'success',
      position: 'top-right',
    })
    await fetchCarInfo()
    await fetchLogs()
    registerState.value = 'success'
  } catch (e) {
    let message = 'Registration failed'
    if (
      typeof e === 'object' &&
      e &&
      'response' in e &&
      e.response &&
      typeof e.response === 'object' &&
      'data' in e.response &&
      e.response.data &&
      typeof e.response.data === 'object' &&
      'message' in e.response.data
    ) {
      message = (e.response.data as { message?: string }).message || message
    } else if (e instanceof Error) {
      message = e.message
    }
    ElNotification({
      title: 'Error',
      message,
      type: 'error',
      position: 'top-right',
    })
    fetchLogs()
    registerState.value = 'error'
  }
}

const fetchCarInfo = async () => {
  carInfoLoading.value = true
  try {
    // Get registration info from portal/register-car endpoint
    const params = { ev_car_profile_id: props.vehicle.id }
    const response = await getRegisterCarInfo(params)
    const data = (response as any)?.data

    // If we have a valid registration with id_portal_car, show it
    if (data?.id_portal_car) {
      carInfo.value = {
        id_portal_car: data.id_portal_car,
        car_number: data.car_number || props.vehicle.plate_number,
        type: data.type || 'ev',
      }
    } else {
      carInfo.value = null
    }
  } catch (e) {
    carInfo.value = null
  } finally {
    carInfoLoading.value = false
  }
}

const handleDeleteCar = async () => {
  try {
    // First, get the registration info to obtain id_portal_car
    const params = { ev_car_profile_id: props.vehicle.id }
    const response = await getRegisterCarInfo(params)
    const id_portal_car = response?.data?.id_portal_car

    if (!id_portal_car) {
      ElNotification({
        title: 'Error',
        message: 'Could not find portal car registration',
        type: 'error',
        position: 'top-right',
      })
      return
    }

    await deleteCar(id_portal_car)
    ElNotification({
      title: 'Success',
      message: 'EV Car deleted successfully!',
      type: 'success',
      position: 'top-right',
    })
    carInfo.value = null
    fetchLogs()
  } catch (e) {
    ElNotification({
      title: 'Error',
      message: 'Failed to delete car',
      type: 'error',
      position: 'top-right',
    })
  }
}

const fetchAll = async () => {
  await Promise.all([fetchCarInfo(), fetchLogs()])
}

onMounted(fetchAll)

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_timeline_item = _resolveComponent("el-timeline-item")!
  const _component_el_timeline = _resolveComponent("el-timeline")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_scrollbar = _resolveComponent("el-scrollbar")!

  return (error.value)
    ? (_openBlock(), _createBlock(CommonError, {
        key: 0,
        errorMessage: error.value,
        onRetryPress: fetchLogs
      }, null, 8, ["errorMessage"]))
    : (isLoading.value && !logs.value.length)
      ? (_openBlock(), _createBlock(Loader, { key: 1 }))
      : (_openBlock(), _createBlock(_component_el_scrollbar, {
          key: 2,
          always: ""
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_row, null, {
              default: _withCtx(() => [
                _createVNode(_component_el_col, { span: 24 }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                      _cache[6] || (_cache[6] = _createElementVNode("h4", { class: "title" }, "Portal EV Car Registration Logs", -1)),
                      (carInfo.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                            _createElementVNode("span", null, [
                              _cache[0] || (_cache[0] = _createElementVNode("b", null, "Portal Car ID:", -1)),
                              _createTextVNode(" " + _toDisplayString(carInfo.value.id_portal_car), 1)
                            ]),
                            _createElementVNode("span", null, [
                              _cache[1] || (_cache[1] = _createElementVNode("b", null, "Car Number:", -1)),
                              _createTextVNode(" " + _toDisplayString(carInfo.value.car_number), 1)
                            ]),
                            _createElementVNode("span", null, [
                              _cache[2] || (_cache[2] = _createElementVNode("b", null, "Type:", -1)),
                              _createTextVNode(" " + _toDisplayString(carInfo.value.type), 1)
                            ]),
                            _cache[4] || (_cache[4] = _createElementVNode("span", { class: "car-info-status" }, [
                              _createElementVNode("b", null, "Status:"),
                              _createElementVNode("span", { style: {"color":"green"} }, " Registered")
                            ], -1)),
                            _createElementVNode("span", _hoisted_3, [
                              _createVNode(_component_el_button, {
                                type: "danger",
                                onClick: handleDeleteCar,
                                loading: carInfoLoading.value
                              }, {
                                default: _withCtx(() => _cache[3] || (_cache[3] = [
                                  _createTextVNode(" Delete car ")
                                ])),
                                _: 1,
                                __: [3]
                              }, 8, ["loading"])
                            ])
                          ]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                            _createElementVNode("span", _hoisted_5, [
                              _createVNode(_component_el_button, {
                                type: "primary",
                                onClick: handleRegisterCar,
                                loading: registerState.value === 'loading'
                              }, {
                                default: _withCtx(() => _cache[5] || (_cache[5] = [
                                  _createTextVNode(" Register EV Car ")
                                ])),
                                _: 1,
                                __: [5]
                              }, 8, ["loading"])
                            ])
                          ]))
                    ]),
                    (logs.value.length)
                      ? (_openBlock(), _createBlock(_component_el_timeline, { key: 0 }, {
                          default: _withCtx(() => [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(logs.value, (log) => {
                              return (_openBlock(), _createBlock(_component_el_timeline_item, {
                                key: log.id,
                                timestamp: _unref(formatDateWithTime)(log.created_at),
                                placement: "top",
                                size: "large"
                              }, {
                                default: _withCtx(() => [
                                  _createElementVNode("div", _hoisted_6, [
                                    _createElementVNode("div", _hoisted_7, [
                                      _cache[7] || (_cache[7] = _createElementVNode("h3", null, "Action:", -1)),
                                      _createElementVNode("p", null, _toDisplayString(log.action), 1)
                                    ]),
                                    _createElementVNode("div", _hoisted_8, [
                                      _cache[8] || (_cache[8] = _createElementVNode("h4", null, "Description:", -1)),
                                      _createElementVNode("p", null, _toDisplayString(log.description), 1)
                                    ]),
                                    _createElementVNode("div", _hoisted_9, [
                                      _cache[9] || (_cache[9] = _createElementVNode("h4", null, "Response:", -1)),
                                      _createElementVNode("p", null, _toDisplayString(log.response), 1)
                                    ])
                                  ])
                                ]),
                                _: 2
                              }, 1032, ["timestamp"]))
                            }), 128))
                          ]),
                          _: 1
                        }))
                      : (_openBlock(), _createElementBlock("h5", _hoisted_10, "No Logs"))
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
}
}

})